/**
 * Song-Bibliothek: Suchen, Sortieren, Anlegen.
 *
 * Tippen auf einen Song oeffnet das Lead Sheet (Uebe-Modus),
 * der Stift-Knopf oeffnet den Editor.
 */
import type { View } from '../app.ts';
import { navigate, refresh } from '../app.ts';
import type { Song } from '../types.ts';
import type { SortKey } from '../db/repo.ts';
import { deleteSong, emptySong, filterSongs, listSongs, newId, saveSong } from '../db/repo.ts';
import { SEPARATOR, withContextMenu } from '../ui/context-menu.ts';
import { toast } from '../ui/toast.ts';
import { activeProject } from '../db/active.ts';
import { demoSong } from '../db/demo.ts';
import { button, h, select, textInput } from '../ui/dom.ts';
import { icon } from '../ui/icons.ts';
import { exportSong } from '../features/pdf/dialog.ts';
import { openAnalysisDialog } from '../features/analysis/dialog.ts';

/** Suche und Sortierung ueberleben den Wechsel in andere Ansichten. */
let query = '';
let sort: SortKey = 'title';

const SORT_OPTIONS = [
  { value: 'title', label: 'Titel' },
  { value: 'artist', label: 'Interpret' },
  { value: 'key', label: 'Tonart' },
  { value: 'updatedAt', label: 'Zuletzt geändert' },
];

/** Zeile "Interpret · Tonart · Tempo · Taktart" fuer die Liste. */
export function songMetaLine(song: Song): string {
  const parts: string[] = [];
  if (song.artist) parts.push(song.artist);
  if (song.key) parts.push(song.key);
  if (song.tempo) parts.push(`${song.tempo} bpm`);
  if (song.timeSignature) parts.push(song.timeSignature);
  return parts.join(' · ');
}

export async function renderLibrary(): Promise<View> {
  const project = await activeProject();
  if (!project) {
    // Noch kein Projekt angelegt: zuerst ins Regal.
    navigate('#/projects', true);
    return { title: 'Projekte', body: h('div'), drawing: null };
  }
  // Id festhalten: in hochgezogenen Funktionen greift die Verengung sonst nicht.
  const projectId = project.id;
  const songs = await listSongs(project.id);

  const listEl = h('ul', { class: 'list' });
  const countEl = h('p', { class: 'muted' });

  function paint(): void {
    const visible = filterSongs(songs, query, sort);
    listEl.textContent = '';
    countEl.textContent =
      songs.length === 0
        ? ''
        : `${visible.length} von ${songs.length} ${songs.length === 1 ? 'Song' : 'Songs'}`;

    if (songs.length === 0) {
      listEl.append(emptyState());
      return;
    }
    if (visible.length === 0) {
      listEl.append(h('li', { class: 'empty', text: 'Nichts gefunden.' }));
      return;
    }
    for (const song of visible) listEl.append(songRow(song));
  }

  function songRow(song: Song): HTMLElement {
    const row = h(
      'li',
      { class: 'list-item' },
      h(
        'button',
        {
          class: 'list-item__main',
          attrs: { type: 'button' },
          on: { click: () => navigate(`#/sheet/${song.id}`) },
        },
        h('span', { class: 'list-item__title', text: song.title || 'Ohne Titel' }),
        h('span', { class: 'list-item__meta', text: songMetaLine(song) || 'Noch keine Angaben' }),
      ),
      h(
        'div',
        { class: 'list-item__actions' },
        h(
          'button',
          {
            class: 'btn btn--ghost btn--icon',
            attrs: { type: 'button', title: 'Bearbeiten', 'aria-label': 'Bearbeiten' },
            on: { click: () => navigate(`#/song/${song.id}`) },
          },
          icon('pencil'),
        ),
      ),
    );
    return withContextMenu(row, song.title || 'Ohne Titel', () => [
      { label: 'Öffnen', action: () => navigate(`#/sheet/${song.id}`) },
      { label: 'Bearbeiten', action: () => navigate(`#/song/${song.id}`) },
      { label: 'Bühnen-Modus', action: () => navigate(`#/stage/song/${song.id}`) },
      { label: 'Als PDF …', action: () => exportSong(song) },
      SEPARATOR,
      {
        label: 'Duplizieren',
        action: async () => {
          const copy = await saveSong({ ...song, id: newId(), title: `${song.title || 'Ohne Titel'} (Kopie)`, createdAt: Date.now() });
          toast('Kopie angelegt');
          navigate(`#/song/${copy.id}`);
        },
      },
      SEPARATOR,
      {
        label: 'Löschen …',
        danger: true,
        action: async () => {
          if (!confirm(`"${song.title || 'Ohne Titel'}" wirklich löschen?`)) return;
          await deleteSong(song.id);
          toast('Song gelöscht');
          refresh();
        },
      },
    ]);
  }

  function emptyState(): HTMLElement {
    return h(
      'li',
      { class: 'empty' },
      h('p', { class: 'empty__title', text: 'Noch keine Songs' }),
      h('p', { text: 'Leg deinen ersten Song an – Titel reicht zum Speichern.' }),
      h(
        'div',
        { class: 'btn-row', style: { justifyContent: 'center', marginTop: '16px' } },
        button('Neuen Song anlegen', () => void createSong(), { class: 'btn btn--primary' }),
        button('Song analysieren', () => analyze()),
        button('Beispielsong laden', async () => {
          const song = await saveSong(demoSong(projectId));
          navigate(`#/sheet/${song.id}`);
        }),
      ),
    );
  }

  /** Aus einer Audiodatei ein Lead Sheet erzeugen (alles auf dem Geraet). */
  function analyze(): void {
    openAnalysisDialog(projectId, (song) => navigate(`#/song/${song.id}`));
  }

  /** Legt sofort einen leeren Song an und springt in den Editor. */
  async function createSong(): Promise<void> {
    const song = await saveSong(emptySong(projectId));
    navigate(`#/song/${song.id}`);
  }

  const searchInput = textInput({
    value: query,
    placeholder: 'Suchen …',
    type: 'search',
    onInput: (value) => {
      query = value;
      paint();
    },
  });

  const body = h(
    'div',
    {},
    h(
      'div',
      { class: 'toolbar' },
      searchInput,
      select(SORT_OPTIONS, sort, (value) => {
        sort = value as SortKey;
        paint();
      }),
      songs.length > 0
        ? h('button', { class: 'btn btn--small', attrs: { type: 'button', title: 'Lead Sheet aus einer Audiodatei erzeugen' }, on: { click: analyze } }, icon('music'), ' Song analysieren')
        : null,
    ),
    countEl,
    listEl,
    songs.length > 0
      ? h('button', {
          class: 'fab',
          text: '+ Neu',
          attrs: { type: 'button' },
          on: { click: () => void createSong() },
        })
      : null,
  );

  paint();

  return {
    title: 'Songs',
    subtitle: project.name || 'Ohne Namen',
    body,
  };
}
